import { css } from 'styled-components';

import type { TypographyStyle } from '../../design-tokens';

export const textStyle = (style: TypographyStyle) => css`
  font-size: ${({ theme }) => theme.typography[style].fontSize};
  line-height: ${({ theme }) => theme.typography[style].lineHeight};
  font-weight: ${({ theme }) => theme.typography[style].fontWeight};
  letter-spacing: ${({ theme }) => theme.typography[style].letterSpacing};
`;

// Heading styles and colours follow the Updated Figma frames: section titles H1, sub-sections H3 SB,
// the "Contents" label H4 SB, all on Neutral/text.
const commonPageContentStyles = css`
  display: grid;
  grid-gap: 0;
  grid-template-columns: auto 1fr;
  grid-template-areas:
    'header  header'
    'sidebar content';
  overflow: hidden;
  height: 100vh;

  .header {
    grid-area: header;
    padding: 2rem;
    max-height: 4rem;
  }

  .sidebar {
    grid-area: sidebar;
    overflow-x: hidden;
    height: calc(100vh - 4rem);
  }

  .scrollable-content {
    grid-area: content;
    overflow-x: hidden;
    height: calc(100vh - 4rem);
  }

  .content {
    margin: 0 auto;
    padding: 24px 16px 32px 16px;

    @media (min-width: 768px) {
      padding: 2.5rem 1.25rem 0;
    }

    @media (min-width: 1620px) {
      width: 75rem;
    }
  }

  .page-container {
    display: flex;
    justify-content: space-between;
    gap: 2.5rem;
    margin-bottom: 2.5rem;
  }

  .page-content {
    flex: 1;
    position: relative;

    .h1 {
      ${textStyle('h1')}
      color: ${({ theme }) => theme.neutralText};
      margin-top: 0;
      margin-bottom: 2.5rem;
    }

    /* The Definition component of the Updated frames draws its title in a 40px text box, the text at
       its top; other titles, and a divider title (65px), keep their height. */
    .page-content-item > .h1:first-child:not(.divider) {
      min-height: 2.5rem;
    }

    .divider {
      border-top: 0.0625rem dashed ${({ theme }) => theme.neutralBorder};
      padding-top: 2.5rem;
    }

    .h2 {
      ${textStyle('h2')}
      color: ${({ theme }) => theme.neutralText};
      margin-top: 0;
      margin-bottom: 1rem;
    }

    .h3 {
      ${textStyle('h3Sb')}
      margin-bottom: 1rem;
      color: ${({ theme }) => theme.neutralText};
      margin-top: 0;
    }

    h4 {
      ${textStyle('h4Sb')}
      color: ${({ theme }) => theme.neutralText};
      margin-top: 0;
      margin-bottom: 0.25rem;
    }

    .h4 {
      ${textStyle('h4')}
      color: ${({ theme }) => theme.neutralTextWeak};
      margin-bottom: 1rem;
    }

    .h5 {
      ${textStyle('h5')}
      color: ${({ theme }) => theme.neutralText};
      margin-top: 0;
    }

    p {
      line-height: 1.5;
      color: ${({ theme }) => theme.neutralTextWeak};
      margin-top: 0;
    }

    strong {
      color: ${({ theme }) => theme.neutralTextWeak};
      font-weight: 600;
    }

    a {
      color: ${({ theme }) => theme.primaryText};
      text-decoration: none;

      &:hover {
        color: ${({ theme }) => theme.primaryText};
      }
    }

    ul {
      line-height: 1.5;
      color: ${({ theme }) => theme.neutralTextWeak};
      list-style: none;
      padding: 0;
      margin-bottom: 2.5rem;

      li {
        margin-bottom: 1rem;

        strong {
          font-weight: 600;
        }
      }
    }

    ol {
      line-height: 1.5;
      color: ${({ theme }) => theme.neutralTextWeak};
      padding: 0;
      margin-bottom: 1.5rem;

      li {
        margin-bottom: 0.5rem;
      }

      &.lower-latin {
        list-style-type: lower-latin;
        list-style-position: inside;
      }

      &.disc {
        list-style-type: disc;
        list-style-position: inside;
      }
    }

    .ordered-list {
      padding-left: 1.75rem;
      counter-reset: num;

      li {
        position: relative;

        &::before {
          content: counter(num) '. ';
          counter-increment: num;
          position: absolute;
          left: -1.25rem;
          top: 0;
          font-size: 1rem;
          line-height: 1.5;
          font-weight: 600;
          color: ${({ theme }) => theme.neutralTextWeak};
        }
      }
    }

    .ordered-list-badge-num {
      font-size: 0.875rem;
      line-height: 1.4285714286;
      padding-left: 2.5rem;
      counter-reset: num;

      li {
        position: relative;

        &::before {
          content: counter(num);
          counter-increment: num;
          position: absolute;
          left: -1.75rem;
          top: 0;
          width: 1.25rem;
          height: 1.25rem;
          border-radius: 0.1875rem;
          text-align: center;
          font-size: 0.6875rem;
          line-height: 1.8;
          color: ${({ theme }) => theme.primaryText};
          background: ${({ theme }) => theme.primaryBackgroundActive};
        }
      }
    }

    .ordered-list-badge-latin {
      font-size: 0.875rem;
      line-height: 1.4285714286;
      padding-left: 2.5rem;
      counter-reset: num;

      li {
        position: relative;

        &::before {
          content: counter(num, upper-latin);
          counter-increment: num;
          position: absolute;
          left: -1.75rem;
          top: 0;
          width: 1.25rem;
          height: 1.25rem;
          border-radius: 0.1875rem;
          text-align: center;
          font-size: 0.6875rem;
          line-height: 1.8;
          color: ${({ theme }) => theme.primaryText};
          background: ${({ theme }) => theme.primaryBackgroundActive};
        }
      }
    }

    .inline-latin {
      display: flex;
      gap: 1.875rem;
      font-size: 0.875rem;
      margin-top: 0.5rem;
      margin-bottom: 0;
    }

    .inside-list {
      list-style-position: inside;
    }

    .disc-list {
      list-style-type: disc;
      padding-left: 1.75rem;

      li {
        margin-bottom: 0;
      }
    }

    /* A frame text whose lines are an ORDERED Figma list renders its numbers. */
    .numbered-list {
      list-style-type: decimal;
      padding-left: 1.5rem;

      li {
        margin-bottom: 0;
      }
    }

    .page-navigation {
      position: sticky;
      top: 5rem;
      left: 100%;
      width: 10rem;

      > h2 {
        ${textStyle('h4Sb')}
        color: ${({ theme }) => theme.neutralText};
        margin: 0 0 1.5rem;
      }

      ul {
        margin: 0;
        padding: 0;
        list-style: none;

        li {
          ${textStyle('actions14Sb')}
          position: relative;
          display: block;
          margin: 0;
          padding: 0.5rem 0.5rem 0.5rem 1.5rem;
          border-left: 0.125rem solid ${({ theme }) => theme.neutralBorder};
          color: ${({ theme }) => theme.neutralTextWeak};
          text-decoration: none;
          cursor: pointer;
          transition:
            color 0.3s ease-in-out,
            border-color 0.3s ease-in-out;

          &.active,
          &:hover {
            border-color: ${({ theme }) => theme.primaryBorder};
            color: ${({ theme }) => theme.primaryText};
          }
        }
      }
    }

    .page-content-with-navigation {
      flex: 1;
      max-width: 62.5rem;

      &-wrapper {
        position: relative;
        display: flex;
        gap: 2.5rem;
        justify-content: space-between;
      }
    }

    .page-content-with-quiz {
      flex: 1;
      max-width: 78.75rem;

      &-wrapper {
        position: relative;
        display: flex;
        gap: 4.2rem;
        justify-content: space-between;
        align-items: flex-start;
      }
    }
  }

  .page-content-item {
    max-width: 43.75rem;
  }

  .table {
    border-top: 0.0625rem solid ${({ theme }) => theme.neutralBorder};
    border-bottom: 0.0625rem dashed ${({ theme }) => theme.neutralBorder};
    border-collapse: collapse;
    width: 100%;

    thead {
      border-bottom: 0.0625rem solid ${({ theme }) => theme.neutralBorder};
      background: ${({ theme }) => theme.neutralBackgroundMedium};
    }

    th {
      font-size: 0.6875rem;
      line-height: 1.272727272727273;
      font-weight: 400;
      color: ${({ theme }) => theme.neutralTextWeak};
      padding: 0.3125rem 0.5rem;
      border-right: 0.0625rem solid ${({ theme }) => theme.neutralBorder};
      white-space: nowrap;
      text-align: start;

      &:last-child {
        border-right: none;
      }
    }

    td {
      font-size: 0.75rem;
      line-height: 1.166666666666667;
      font-weight: 400;
      color: ${({ theme }) => theme.neutralText};
      padding: 0.875rem 1rem 0.875rem 0.5rem;
    }

    span {
      color: ${({ theme }) => theme.neutralTextWeak};
      margin-right: 0.125rem;
    }

    &-colors {
      border-bottom: none;

      tr {
        border-bottom: 1px solid ${({ theme }) => theme.neutralBorder};

        &:last-child {
          border-bottom: none;
        }
      }

      td {
        ${textStyle('h5')}
        color: ${({ theme }) => theme.neutralTextWeak};
        padding: 1.5rem 0.5rem;
      }

      .role-color {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        background-color: ${({ theme }) => theme.neutralBackgroundMedium};
        border-radius: 0.5rem;
        padding: 0.375rem;
        margin-left: 0.75rem;
      }

      .color {
        border-radius: 0.208125rem;
        width: 1.25rem;
        height: 1.25rem;
        display: block;
      }
    }

    // The older General Components frames set their variant-table cells in three styles:
    // Paragraph/Medium (the default), H5 on every cell and token chip (.text-h5) or on the first
    // column only (.text-h5-lead, Buttons), and Body/12 (.text-body12, the Links height table).
    &-variants {
      tr {
        border-bottom: 1px solid ${({ theme }) => theme.neutralBorder};
      }

      td {
        ${textStyle('paragraphMedium')}
        color: ${({ theme }) => theme.neutralText};
      }

      &.text-h5 td,
      &.text-h5 .role-color,
      &.text-h5-lead td:first-child {
        ${textStyle('h5')}
      }

      // The H5 table rows of the Communication, Navigation and Selection frames: 16px above and
      // below inside a 1px bottom stroke, 8px in, and a 194px text column per cell (a 234px cell
      // with 40px right padding and a 16px gap), the last cell of a two-column table 660px.
      &.text-h5 {
        table-layout: fixed;
      }

      &.text-h5 td {
        padding: 1rem 3rem 0.9375rem 0.5rem;
      }

      &.text-h5 td:last-child {
        padding-right: 0.5rem;
      }

      &.text-h5 td:nth-child(2):last-child {
        padding-right: 5.125rem;
      }

      // The Paragraph/Medium and Body/12 tables of the Links and Form Elements frames: equal columns,
      // 13px above the text and 13px below it inside the 1px stroke, 8px in; a 24px chip row keeps
      // the same height.
      &:not(.text-h5, .text-h5-lead) {
        table-layout: fixed;

        td {
          padding: 0.8125rem 0.5rem 0.75rem;
        }

        td:has(.role-color) {
          padding-block: 0.6875rem 0.625rem;
        }
      }

      // The H5 tables' token chips: 4px padding and radius on Neutral/background-medium, 24px tall.
      &.text-h5 .role-color {
        padding: 0.25rem;
        border-radius: 0.25rem;
        background-color: ${({ theme }) => theme.neutralBackgroundMedium};
      }

      &.text-body12 td {
        ${textStyle('body12')}
      }

      .role-color {
        font-size: 0.75rem;
        font-weight: 500;
        line-height: 1.166666666666667;
        background-color: ${({ theme }) => theme.neutralBackground};
        display: inline-block;
        padding: 0.3125rem 0.375rem;
        border-radius: 0.1875rem;
      }
    }
  }

  .checkmark-container {
    display: inline-flex;
    gap: 0.5rem;
    align-items: center;
    text-transform: uppercase;

    .checkmark {
      color: ${({ theme }) => theme.successText};
    }
  }

  .content-item {
    max-width: 62.5rem;
    border-bottom: 1px dashed ${({ theme }) => theme.neutralBorder};
    margin-bottom: 1.5rem;
  }

  /* The Do / Don't card frame (I13608:136326;9935:89852): a 1px inside stroke round the picture and the
     description, radius 0 0 8 8, and the 2px status line drawn over its top edge. Both are overlays, so the
     card is as tall as its picture and description, as in the frame. */
  .ex-card {
    position: relative;
    height: 100%;
    display: flex;
    flex-direction: column;
    border-radius: 0 0 0.5rem 0.5rem;
    overflow: hidden;

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      border: 0.0625rem solid ${({ theme }) => theme.neutralBorder};
      border-radius: inherit;
      pointer-events: none;
    }

    &.true::before,
    &.false::before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      left: 0;
      z-index: 1;
      height: 0.125rem;
    }

    &.true::before {
      background-color: ${({ theme }) => theme.successBackgroundStrong};
    }

    &.false::before {
      background-color: ${({ theme }) => theme.alertBackgroundStrong};
    }

    &s {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      column-gap: 1.5rem;
      row-gap: 2.5rem;
      margin-bottom: 2.5rem;

      /* The Do and Don't descriptions of every Updated frame are H4 on Neutral/text-weak. */
      p {
        ${textStyle('h4')}
        margin-bottom: 0;
      }

      figure {
        margin-bottom: 0;
      }
    }

    /* The pictures carry their frame's width and height; without max-width they keep 488px in a narrower card. */
    &-img {
      display: block;
      overflow: hidden;
      max-width: 100%;
      height: auto;
    }

    &-desc {
      flex-direction: column;
      align-items: flex-start;
      display: flex;
      gap: 1rem;
      padding: 1.5rem;
      flex: 1;

      /* The Do / Don't card component sets its text block at a fixed 48px, the text at its top. */
      > p,
      > .disc-list {
        min-height: 3rem;
      }

      .disc-list {
        margin: 0;
      }
    }
  }

  .number-card {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    &s {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      column-gap: 1.5rem;
      row-gap: 2.5rem;
      margin-bottom: 2.5rem;

      // The example grids of the Communication, Form Elements, Navigation and Selection frames stack
      // their rows 24px apart.
      &.row-gap-24 {
        row-gap: 1.5rem;
      }

      img {
        max-width: 100%;
        height: auto;
      }

      strong {
        display: block;
        font-size: 1rem;
        letter-spacing: -0.01em;
      }

      p {
        font-size: 0.875rem;
        line-height: 1.428571428571429;
        margin-top: 0.5rem;
        margin-bottom: 1.25rem;

        &:last-child {
          margin-bottom: 0;
        }
      }

      // A caption with no title under it sits 16px below the picture (the card gap), as the frames draw it.
      .number-card-desc > p:first-child {
        margin-top: 0;
      }
    }

    &-desc-row {
      strong {
        font-size: 0.875rem;
        display: inline-block;
      }

      p {
        margin-top: 0;
        margin-bottom: 0;
      }
    }

    &-img {
      display: flex;
      position: relative;
    }

    .number-circle {
      position: absolute;
      top: 1rem;
      left: 1rem;
      width: 1.5rem;
      height: 1.5rem;
      border-radius: 50%;
      background: ${({ theme }) => theme.primaryBackgroundStrong};
      color: ${({ theme }) => theme.primaryTextStrong};
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 0.875rem;
      font-weight: 400;
      line-height: 1.142857142857143;
    }
  }

  .examples-wrapper {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 1.5rem;
    row-gap: 2rem;
    margin-bottom: 2.5rem;

    &-item {
      display: flex;
      gap: 1rem;
      flex-direction: column;
      margin-bottom: 0;
    }

    img {
      max-width: 100%;
      height: auto;
    }

    p {
      font-size: 0.875rem;
      line-height: 1.428571428571429;
      margin-bottom: 0;
    }
  }

  figure {
    display: grid;
    gap: 1rem;
    margin: 0 0 2.5rem;

    img {
      max-width: 100%;
      height: auto;
    }

    figcaption {
      ${textStyle('paragraphMedium')}
      color: ${({ theme }) => theme.neutralTextWeak};
    }
  }

  .figure-sm {
    gap: 1.5rem;

    figcaption {
      max-width: 43.75rem;
      font-size: 0.875rem;
      line-height: 1.428571428571429;
      color: ${({ theme }) => theme.neutralTextWeak};
    }
  }

  .reccomendation {
    &s {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 2.5rem 1.5rem;
    }

    &-item {
      border: 1px solid ${({ theme }) => theme.neutralBorder};
      border-radius: 0.5rem;
      box-shadow: 0px 2px 2px 0px #141c2c0a; /* tokens-allow: pending design decision */
      padding: 1.5rem 2rem;
      display: flex;
      flex-direction: column;
      align-items: start;
      gap: 1.5rem;

      .disc-list {
        margin: 0;
      }
    }
  }

  /* Block, so an image that ends a block leaves no line-box gap under it. */
  .img-responsive {
    display: block;
    max-width: 100%;
    height: auto;
  }

  .pseudo-list {
    margin: 0 0 1.5rem;
    position: relative;
    padding-left: 1.75rem;
    font-size: 0.875rem;

    p,
    li {
      margin-bottom: 1rem;
      line-height: 1.428571428571429;
    }

    &-nr {
      position: absolute;
      left: 0;
      top: 0;
      width: 1.25rem;
      height: 1.25rem;
      border-radius: 0.1875rem;
      text-align: center;
      font-size: 0.6875rem;
      line-height: 1.8;
      color: ${({ theme }) => theme.primaryText};
      background: ${({ theme }) => theme.primaryBackgroundActive};
      font-optical-sizing: auto;
    }
  }

  .title-with-download-button {
    display: flex;
    justify-content: space-between;
    gap: 2rem;
    align-items: center;
    margin-bottom: 1.5rem;

    h2 {
      margin-bottom: 0 !important;
    }
  }

  .download-assets {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border: 1px solid ${({ theme }) => theme.neutralBorder};
    border-radius: 0.375rem;
    padding: 0.6875rem 1rem;

    &-buttons,
    &-info {
      display: flex;
      gap: 0.5rem;
    }

    &-icon {
      padding: 0.25rem;

      svg {
        display: block;
      }
    }

    &-title {
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: 0.25rem;

      strong {
        font-size: 0.875rem;
        font-weight: 600;
        line-height: 1.428571428571429;
        color: ${({ theme }) => theme.neutralTextWeak};
      }

      p {
        font-size: 0.875rem;
        font-weight: 400;
        line-height: 1.428571428571429;
        color: ${({ theme }) => theme.neutralTextWeak};
        margin-bottom: 0;

        span {
          color: ${({ theme }) => theme.neutralTextWeak};
        }
      }
    }
  }

  .background-color {
    display: block;
    width: 100%;
    height: 1.25rem;
    border-radius: 0.25rem;

    &s {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      column-gap: 1.5rem;
      row-gap: 2.5rem;
      margin-bottom: 2.5rem;
    }

    &s-item {
      display: flex;
      gap: 1rem;
      flex-direction: column;
      margin-bottom: 0;

      strong {
        display: block;
        letter-spacing: -0.01em;
      }

      p {
        font-size: 0.875rem;
        line-height: 1.428571428571429;
        margin-bottom: 0;

        span {
          font-weight: 500;
        }
      }
    }
  }

  .news {
    border: 1px solid ${({ theme }) => theme.neutralBorder};
    border-radius: 0.25rem;
    display: grid;
    gap: 1.5rem;
    padding: 2.5rem 2.8125rem;
    margin-bottom: 2.5rem;
    grid-template-columns: repeat(2, minmax(0, 1fr));

    @media (min-width: 1620px) {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    &-item {
      display: flex;
      flex-direction: column;
      gap: 1.25rem;
      align-items: flex-start;
    }

    &-img {
      display: block;
      overflow: hidden;
      border-radius: 0.5rem;
    }

    &-desc {
      display: flex;
      flex-direction: column;
      gap: 0.625rem;
      align-items: flex-start;

      /* The Updated frame sets the card titles in Public Sans Bold. */
      strong {
        font-size: 1rem;
        font-weight: 700;
        line-height: 1.5;
        color: ${({ theme }) => theme.neutralText};
      }

      p {
        font-size: 0.875rem;
        font-weight: 400;
        line-height: 1.428571428571429;
        margin-bottom: 0;
      }
    }

    &-data {
      font-size: 0.625rem;
      font-weight: 400;
      line-height: 1.2;
      color: ${({ theme }) => theme.neutralTextWeak};
      padding: 0.375rem 0.625rem;
      border-radius: 0.875rem;
      border: 1px solid ${({ theme }) => theme.neutralBorder};
    }
  }

  // Text styles a frame sets on a single paragraph, list or heading. Kept last: they win over the
  // element rules above (.ex-card-desc p, .number-card p) by source order at equal specificity.
  .page-content :is(p, ul, ol, li, div, h4) {
    &.text-paragraph-medium {
      ${textStyle('paragraphMedium')}
    }

    &.text-paragraph-large {
      ${textStyle('paragraphLarge')}
    }

    &.text-body14 {
      ${textStyle('body14')}
    }

    &.text-h4-sb {
      ${textStyle('h4Sb')}
    }
  }
`;
export default commonPageContentStyles;
